<template>
  <div class="container">
    <div class="dropdown-container">
      <!-- FSM -->
      <div class="dropdown-item">
        <label for="fsmSelect">FSM</label>
        <select id="fsmSelect" v-model="selectedFsm" @change="onFsmChange" :disabled="isFsmLocked">
          <option value="">Select FSM</option>
          <option v-if="isOtherUser" :value="ALL">All FSMs</option>
          <option v-for="fsm in fsmList" :key="fsm.profile_id" :value="String(fsm.profile_id)">
            {{ fsm.name }}
          </option>
        </select>
      </div>

      <!-- ASM -->
      <div class="dropdown-item">
        <label for="asmSelect">ASM</label>
        <select id="asmSelect" v-model="selectedAsm" @change="onAsmChange" :disabled="isAsmLocked">
          <option value="">Select ASM</option>
          <option v-if="isFsmUser || isOtherUser" :value="ALL">All ASMs</option>

          <option v-for="asm in asmList" :key="asm.profile_id" :value="String(asm.profile_id)">
            {{ asm.name }}
          </option>
        </select>
      </div>

      <!-- Distributor -->
      <div class="dropdown-item">
        <label for="distributorSelect">Distributor</label>
        <select id="distributorSelect" v-model="selectedDistributor" :disabled="isDistributorLocked">
          <option value="">Select Distributor</option>
          <option v-if="isFsmUser || isAsmUser || isOtherUser" :value="ALL">All Distributors</option>

          <option v-for="distributor in distributorList" :key="distributor.profile_id"
            :value="String(distributor.profile_id)">
            {{ distributor.name }}
          </option>
        </select>
      </div>

      <!-- Dates -->
      <div class="dropdown-item">
        <label>Start Date</label>
        <input type="date" v-model="startDate" :max="currentDate" @change="handleDateChange"
          @blur="validateStartDate(true)" />
      </div>

      <div class="dropdown-item">
        <label>End Date</label>
        <input type="date" v-model="endDate" :max="currentDate" @change="handleDateChange"
          @blur="validateEndDate(true)" />
      </div>

      <!-- Search -->
      <div class="dropdown-item search-container">
        <button @click="performSearch" class="btn button-default mt-4" :disabled="loading">
          {{ loading ? "Searching..." : "Search" }}
        </button>
      </div>

      <!-- Export -->
      <div class="dropdown-item search-container">
        <button @click="exportToExcel" class="btn button-export mt-4" :disabled="!reportData.length">
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- ✅ Table (dynamic products) -->
    <div v-if="!loading && reportData.length && items.length" class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered">
          <thead>
            <tr class="header-row-1">
              <th rowspan="2">FSM</th>
              <th rowspan="2">ASM</th>
              <th rowspan="2">Distributor</th>
              <!-- <th rowspan="2">Date</th> -->

              <!-- ✅ merged products header -->
              <th :colspan="items.length" class="text-center">Products</th>

              <th rowspan="2">Gross Total</th>
            </tr>

            <tr class="header-row-2">
              <!-- ✅ dynamic product headers -->
              <th v-for="it in items" :key="it.item_id" class="text-center">
                {{ it.item_name }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, i) in reportData" :key="i">
              <td>{{ row.fsm_name || "-" }}</td>
              <td>{{ row.asm_name || "-" }}</td>
              <td>{{ row.distributor_name || "-" }}</td>
              <!-- <td>{{ row.invoice_date || "-" }}</td> -->

              <!-- ✅ dynamic product values -->
              <td v-for="it in items" :key="it.item_id" class="text-end">
                {{ formatQty(row?.[String(it.item_id)] ?? 0) }}
              </td>

              <td class="text-end">{{ formatMoney(row.total_net ?? 0) }}</td>
            </tr>

            <!-- ✅ totals row -->
            <tr v-if="reportData.length" class="total-row">
              <td colspan="3" class="text-end"><strong>Total</strong></td>

              <td v-for="it in items" :key="it.item_id" class="text-end">
                <strong>{{ formatQty(columnTotal(String(it.item_id))) }}</strong>
              </td>

              <td class="text-end"><strong>{{ formatMoney(grandTotalNet) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!loading && !reportData.length" class="no-data-message">
      <p>No data found. Please select filters and click Search.</p>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchAsmFsmWiseReport,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: "AsmFsmWiseSalesForm",

  data() {
    const ALL = "__all__";
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      token: "",
      fsmList: [],
      asmList: [],
      distributorList: [],

      selectedFsm: "",
      selectedAsm: "",
      selectedDistributor: "",

      startDate: currentDate,
      endDate: currentDate,
      currentDate,

      loading: false,

      // ✅ dynamic
      items: [],      // [{item_id,item_name,sort_id}]
      reportData: [], // rows with keys as "item_id"

      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      isFsmLocked: false,
      isAsmLocked: false,
      isDistributorLocked: false,

      ALL,
      redirecting: false,
    };
  },

  computed: {
    isDistributorUser() { return this.roleKey === "DIS"; },
    isAsmUser() { return this.roleKey === "ASM"; },
    isFsmUser() { return this.roleKey === "FSM"; },
    isOtherUser() { return this.roleKey === "OTHER"; },

    grandTotalNet() {
      return this.reportData.reduce((sum, r) => sum + (Number(r.total_net) || 0), 0);
    },
  },

  mounted() {
    this.initLoginAndLoadFsm();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },

    validateFiltersOneByOne() {
      if (!this.selectedFsm) { this.toast("error", "Please select FSM."); return false; }
      if (!this.selectedAsm) { this.toast("error", "Please select ASM."); return false; }
      if (!this.selectedDistributor) { this.toast("error", "Please select Distributor."); return false; }
      return true;
    },


    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;

      // block manual formats like 2025/11/31
      if (dateStr.includes("/")) return false;

      // must be YYYY-MM-DD
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;

      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);

      // detect overflow dates like 2025-11-31
      const dt = new Date(y, mo - 1, d);
      return (
        dt.getFullYear() === y &&
        dt.getMonth() === mo - 1 &&
        dt.getDate() === d
      );
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) return true; // required check happens in validateDatesForSearch()

      if (!this.isValidISODate(this.startDate)) {
        if (showToast) this.toast("error", "Invalid Start Date");
        this.startDate = "";
        return false;
      }

      if (this.startDate > this.currentDate) {
        if (showToast) this.toast("error", "Start Date cannot be in the future");
        this.startDate = "";
        return false;
      }

      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) return true; // required check happens in validateDatesForSearch()

      if (!this.isValidISODate(this.endDate)) {
        if (showToast) this.toast("error", "Invalid End Date");
        this.endDate = "";
        return false;
      }

      if (this.endDate > this.currentDate) {
        if (showToast) this.toast("error", "End Date cannot be in the future");
        this.endDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      // validate each field silently first
      this.validateStartDate(false);
      this.validateEndDate(false);

      // if both exist, validate range
      if (this.startDate && this.endDate) {
        if (this.endDate < this.startDate) {
          this.toast("error", "Invalid date range picked.");
          this.endDate = "";
        }
      }
    },

    validateDatesForSearch() {
      // REQUIRED
      if (!this.startDate) { this.toast("error", "Please select valid Start Date"); return false; }
      if (!this.endDate) { this.toast("error", "Please select valid End Date"); return false; }

      // VALID
      if (!this.validateStartDate(true)) return false;
      if (!this.validateEndDate(true)) return false;

      // RANGE
      if (this.endDate < this.startDate) {
        this.toast("error", "Invalid date range picked.");
        return false;
      }

      return true;
    },


    // =========================
    // ✅ AUTH / UNAUTH HANDLING
    // =========================
    bodyOf(x) { return x?.data ?? x; },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },


    // isUnauthorizedAny(x) {
    //   const http = x?.response?.status ?? x?.status;

    //   // message from either axios error or normal response body
    //   const msg = this.msgOf(x).toLowerCase();

    //   return (
    //     http === 401 ||
    //     http === 419 ||
    //     http === 440 ||
    //     http === 498 ||
    //     /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/.test(msg)
    //   );
    // },

    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();

      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;

      // ✅ KEY FIX:
      // If backend wrongly uses 401 for business cases, don't logout unless message clearly indicates token/auth issue.
      if (http === 401) return authMsg.test(msg);

      return (
        http === 419 ||
        http === 440 ||
        http === 498 ||
        authMsg.test(msg)
      );
    },


    redirectToLogin(message) {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", message || "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },

    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },




    // =========================
    // ✅ INIT
    // =========================
    async initLoginAndLoadFsm() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token_set") || "";
        if (!token) {
          this.redirectToLogin("Session expired. Please login again.");
          return;
        }
        this.token = token;

        this.myProfileId = Number(localStorage.getItem("profile_id") || 0);

        const labelFromStorage =
          localStorage.getItem("user_label") ||
          localStorage.getItem("userLabel") ||
          localStorage.getItem("role") ||
          "";

        this.userLabel = String(labelFromStorage);
        const labelUpper = String(this.userLabel || "").toUpperCase().trim();

        if (
          labelUpper === "DISTRIBUTOR" ||
          labelUpper.startsWith("DISTRIBUTOR") ||
          labelUpper === "DIS" ||
          labelUpper.startsWith("DIS_")
        ) this.roleKey = "DIS";
        else if (labelUpper === "ASM" || labelUpper.startsWith("ASM_")) this.roleKey = "ASM";
        else if (labelUpper === "FSM" || labelUpper.startsWith("FSM_")) this.roleKey = "FSM";
        else this.roleKey = "OTHER";

        await this.loadFsmList();
        if (this.redirecting) return;

        this.selectedFsm = "";
        this.selectedAsm = "";
        this.selectedDistributor = "";
        this.asmList = [];
        this.distributorList = [];
        this.isDistributorLocked = false;

        // ✅ clear report
        this.items = [];
        this.reportData = [];

      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error("[AsmFsmWise] init error:", err?.response?.data || err);
        this.toast("error", "Failed to initialize page.");
      } finally {
        this.loading = false;
      }
    },

    // =========================
    // ✅ DROPDOWN LOADERS
    // =========================
    async loadFsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchFsmList(profileId, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        // this.fsmList = body?.data || [];
        this.fsmList = this.onlyActive(body?.data || []);
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load FSM list.");
      }
    },

    async onFsmChange() {
      this.selectedAsm = "";
      this.selectedDistributor = "";
      this.asmList = [];
      this.distributorList = [];
      this.isDistributorLocked = false;

      // ✅ clear report
      this.items = [];
      this.reportData = [];

      if (this.selectedFsm === this.ALL) return;
      if (!this.selectedFsm) return;

      await this.loadAsmList();
    },

    async onAsmChange() {
      this.selectedDistributor = "";
      this.distributorList = [];
      this.isDistributorLocked = false;

      // ✅ clear report
      this.items = [];
      this.reportData = [];

      if (this.selectedAsm === this.ALL) return;
      if (!this.selectedAsm) return;

      await this.loadDistributorList();
    },


    async loadAsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchAsmList(profileId, this.selectedFsm, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);

        // ✅ if backend sends token-invalid message inside HTTP 200
        if (this.handleUnauthorized(body)) return;

        // const list = body?.data || [];
        const list = this.onlyActive(body?.data || []);
        this.asmList = list;

        if (!list.length) {


          // clear downstream dropdowns
          this.selectedAsm = "";
          this.selectedDistributor = "";
          this.distributorList = [];
          this.isDistributorLocked = false;

          // clear report
          this.items = [];
          this.reportData = [];
          return;
        }
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load ASM list.");
      }
    },


    // =========================
    // ✅ FORMATTERS
    // =========================


    async loadDistributorList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchDistributorList(profileId, this.selectedAsm, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);

        // ✅ if backend sends token-invalid message inside HTTP 200
        if (this.handleUnauthorized(body)) return;

        // let list = body?.data || [];
        let list = this.onlyActive(body?.data || []);

        if (!list.length) {


          this.selectedDistributor = "";
          this.distributorList = [];
          this.isDistributorLocked = false;

          // clear report
          this.items = [];
          this.reportData = [];
          return;
        }

        // Distributor login: lock to own distributor
        if (this.isDistributorUser && this.myProfileId) {
          const mine = list.find(d => Number(d.profile_id) === Number(this.myProfileId));
          if (mine) {
            list = [mine];
            this.selectedDistributor = String(mine.profile_id);
            this.isDistributorLocked = true;
          }
        }

        this.distributorList = list;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load Distributor list.");
      }
    },

    // ✅ Treat missing flag as active (so you don’t accidentally hide if API doesn't send it)
    isActiveUser(x) {
      const v =
        x?.is_active ??
        x?.user_is_active ??
        x?.app_user_is_active ??
        x?.active;

      if (v === undefined || v === null) return true;   // no flag => keep
      return v === true || Number(v) === 1;
    },

    onlyActive(list) {
      return (Array.isArray(list) ? list : []).filter(this.isActiveUser);
    },



    formatQty(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0";
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    },
    formatMoney(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0.00";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    columnTotal(itemIdStr) {
      return this.reportData.reduce((sum, r) => sum + (Number(r?.[itemIdStr]) || 0), 0);
    },

    // =========================
    // ✅ REPORT SEARCH (dynamic)
    // =========================
    async performSearch() {
      this.token = localStorage.getItem("token_set") || "";
      if (!this.token) return this.redirectToLogin();

      if (!this.validateFiltersOneByOne()) return;
      if (!this.validateDatesForSearch()) return;


      this.loading = true;
      this.items = [];
      this.reportData = [];

      try {
        const payload = {
          start_date: this.startDate || null,
          end_date: this.endDate || null,
          fsm_profile_id: this.selectedFsm === this.ALL ? null : this.selectedFsm,
          asm_profile_id: this.selectedAsm === this.ALL ? null : this.selectedAsm,
          distributor_profile_id: this.selectedDistributor === this.ALL ? null : this.selectedDistributor,
        };

        const apiRes = await fetchAsmFsmWiseReport(this.token, payload);
        if (this.handleUnauthorized(apiRes)) return;

        const res = this.bodyOf(apiRes);
        if (this.handleUnauthorized(res)) return;

        if (!res || res.status !== true) {
          this.toast("error", res?.message || "Error loading report.");
          return;
        }

        const items = Array.isArray(res.items) ? res.items : [];
        const rows = Array.isArray(res.data) ? res.data : [];

        // ✅ items must exist for dynamic columns
        this.items = items
          .map(x => ({
            item_id: String(x.item_id),
            item_name: String(x.item_name ?? ""),
            sort_id: Number(x.sort_id ?? 0),
          }))
          .sort((a, b) => a.sort_id - b.sort_id);

        if (!rows.length) {
          this.toast("error", "No data found for selected filters.");
          return;
        }

        // ✅ ensure row has all item keys
        this.reportData = rows.map(r => {
          const out = { ...r };
          this.items.forEach(it => {
            const k = String(it.item_id);
            out[k] = Number(r?.[k]) || 0;
          });
          out.total_net = Number(r?.total_net) || 0;
          return out;
        });

        this.toast("success", "Report loaded successfully.");
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", err?.response?.data?.message || "Error loading report.");
      } finally {
        this.loading = false;
      }
    },

    // =========================
    // ✅ EXCEL EXPORT (dynamic)
    // =========================
    exportToExcel() {
      if (!this.reportData.length || !this.items.length) {
        this.toast("error", "No data to export.");
        return;
      }

      // helper to get selected name
      const getName = (list, id) => {
        if (!id || id === this.ALL) return "All";
        const item = list.find(x => String(x.profile_id) === String(id));
        return item ? item.name : "";
      };

      const fsmName = getName(this.fsmList, this.selectedFsm);
      const asmName = getName(this.asmList, this.selectedAsm);
      const distributorName = getName(this.distributorList, this.selectedDistributor);

      // FILTER SECTION
      const filterRows = [
        ["ASM / FSM Wise Sales Report"],
        [],
        ["FSM", fsmName],
        ["ASM", asmName],
        ["Distributor", distributorName],
        ["Start Date", this.startDate],
        ["End Date", this.endDate],
        [],
      ];

      // ===== TABLE HEADERS =====
      const headerRow1 = [
        "FSM", "ASM", "Distributor", 
        "Products", ...Array(this.items.length - 1).fill(""), 
        "Gross Total",
      ];
      const headerRow2 = ["", "", "", ...this.items.map(it => it.item_name), ""];

      const dataRows = this.reportData.map(r => ([
        r.fsm_name || "-",
        r.asm_name || "-",
        r.distributor_name || "-",
        ...this.items.map(it => Number(r[String(it.item_id)]) || 0),
        Number(r.total_net) || 0,
      ]));

      const totalRow = [
        "TOTAL", "", "",
        ...this.items.map(it => this.columnTotal(String(it.item_id))),
        this.grandTotalNet,
      ];

      // ===== GENERATED SECTION =====
      const firstName = localStorage.getItem('first_name') || "";
      const lastName = localStorage.getItem('last_name') || "";
      const generatedBy = `${firstName} ${lastName}`.trim();
      const generatedAt = new Date().toLocaleString();

      const generatedRows = [
        [], // empty row
        ["Generated By", generatedBy],
        ["Generated At", generatedAt],
      ];

      const aoa = [...filterRows, headerRow1, headerRow2, ...dataRows, totalRow, ...generatedRows];

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // header row start index after filters
      const startRow = filterRows.length;

      ws["!merges"] = [
        { s: { r: startRow, c: 0 }, e: { r: startRow + 1, c: 0 } },
        { s: { r: startRow, c: 1 }, e: { r: startRow + 1, c: 1 } },
        { s: { r: startRow, c: 2 }, e: { r: startRow + 1, c: 2 } },

        // Products merged
        { s: { r: startRow, c: 3 }, e: { r: startRow, c: 3 + this.items.length - 1 } },

        // Gross total merged
        { s: { r: startRow, c: 3 + this.items.length }, e: { r: startRow + 1, c: 3 + this.items.length } },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ASM_FSM_Wise_Sales");
      XLSX.writeFile(wb, `ASM_FSM_Wise_Sales_${this.startDate}_to_${this.endDate}.xlsx`);
      this.toast("success", "Excel exported successfully.");
    },
  },
};
</script>

<style scoped>
/* Scrollable Table */
.table-scroll {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
}

.table-scroll table {
  min-width: 100%;
}

/* Export Button */
.btn.button-export {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #28a745;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.btn.button-export:hover {
  background-color: #218838;
  transform: scale(1.05);
}

.btn.button-export:active {
  transform: scale(0.95);
}

.btn.button-export:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
}

html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: calc(100vh - 20px);
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
}

.dropdown-item {
  flex: 1;
  min-width: 180px;
  max-width: 250px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select,
input[type="date"],
input[type="text"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

select:focus,
input[type="date"]:focus,
input[type="text"]:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.search-container {
  display: flex;
  align-items: flex-end;
  justify-content: left;
}

.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.btn.button-default:hover {
  background-color: #0056b3;
  transform: scale(1.05);
}

.btn.button-default:active {
  transform: scale(0.95);
}

.btn.button-default:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
}

.card {
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}

.table-scroll {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
  position: relative;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

/* General header cell styling */
.table thead th {
  background-color: #f9f9f9;
  font-weight: bold;
  z-index: 3;
}

/* First header row sticky */
.table thead .header-row-1 th {
  position: sticky;
  top: 0;
}

/* Second header row sticky */
.table thead .header-row-2 th {
  position: sticky;
  top: 46px;
  /* keep same */
}

.table td {
  background-color: #ffffff;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: #f8f9fa;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.text-center {
  text-align: center;
}

.text-end {
  text-align: right;
}

.total-row td {
  background: #f1f3f5;
  font-weight: 700;
}
</style>

